import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import {
  getPrivacyRequest,
  suppressProfileForRequest,
  restoreProfileForRequest,
  previewPrivacyResolution,
  resolvePrivacyRequest,
} from "../../api/clientVendorDirectoryPrivacyApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.privacy";

function statusBadgeClass(status) {
  if (status === "resolved" || status === "approved") return "cvd-badge--published";
  if (["rejected", "withdrawn", "closed"].includes(status)) return "cvd-badge--suspended";
  if (["under_review", "awaiting_information", "identity_check"].includes(status)) return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function eventField(row, snakeKey, camelKey) {
  return row?.[camelKey] ?? row?.[snakeKey] ?? null;
}

export default function ClientVendorDirectoryPrivacyDetailPage() {
  const { clientViewId, requestId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t, locale } = useLocale();
  const basePath = `${serviciosBase}/privacidad`;
  const viewName = activeView?.name || "CasaMapa";

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerTone, setBannerTone] = useState("success");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getPrivacyRequest(clientViewId, requestId);
      setDetail(body);
      setPreview(null);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.detail.loadError`));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, requestId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const showBanner = (message, tone = "success") => {
    setBannerMessage(message);
    setBannerTone(tone);
    window.setTimeout(() => setBannerMessage(""), 5000);
  };

  const runAction = async (action, successMessage) => {
    setBusy(true);
    try {
      await action();
      showBanner(successMessage);
      await load();
    } catch (error) {
      showBanner(error.message || t(`${COPY_PREFIX}.detail.actionError`), "warning");
    } finally {
      setBusy(false);
    }
  };

  const runPreview = () =>
    runAction(async () => {
      const body = await previewPrivacyResolution(clientViewId, requestId, {
        expectedPreviewVersion: detail?.request?.previewVersion,
      });
      setPreview(body.preview);
    }, t(`${COPY_PREFIX}.detail.previewSuccess`));

  const resolve = (resolutionType, successMessage) =>
    runAction(async () => {
      await resolvePrivacyRequest(clientViewId, requestId, {
        resolutionType,
        resolutionReason: resolutionType === "rejected" ? "admin_rejection" : "admin_resolution",
        expectedPreviewVersion: detail?.request?.previewVersion,
        anonymizeRequester: resolutionType === "approved",
      });
    }, successMessage);

  const suppressProfile = () =>
    runAction(async () => {
      await suppressProfileForRequest(clientViewId, requestId, { reason: "privacy_review" });
    }, t(`${COPY_PREFIX}.detail.suppressSuccess`));

  const restoreProfile = () =>
    runAction(async () => {
      await restoreProfileForRequest(clientViewId, requestId);
    }, t(`${COPY_PREFIX}.detail.restoreSuccess`));

  if (loading) {
    return (
      <div className="page-content cvd-admin cvd-privacy">
        <ClientPortalLoading message={t(`${COPY_PREFIX}.detail.loading`)} />
      </div>
    );
  }

  if (errorMessage && !detail?.request) {
    return (
      <div className="page-content cvd-admin cvd-privacy">
        <ClientPortalError message={errorMessage} onRetry={load} />
      </div>
    );
  }

  const request = detail?.request;

  return (
    <div className="page-content cvd-admin cvd-privacy">
      <div className="cvd-admin__header">
        <div>
          <p className="cvd-run-detail__eyebrow">{request?.trackingReference || requestId}</p>
          <h1 className="cvd-admin__title">{request?.vendorName || t(`${COPY_PREFIX}.detail.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.intro`, { viewName })}</p>
          <div className="cvd-run-detail__meta">
            <span className={`cvd-badge ${statusBadgeClass(request?.status)}`}>
              {t(`${COPY_PREFIX}.status.${request?.status}`, {}, request?.status)}
            </span>
            <span className="cvd-badge cvd-badge--draft">
              {t(`${COPY_PREFIX}.requestType.${request?.requestType}`, {}, request?.requestType)}
            </span>
            {request?.profileSuppressed ? (
              <span className="cvd-badge cvd-badge--warning">{t(`${COPY_PREFIX}.suppressedBadge`)}</span>
            ) : null}
          </div>
        </div>
        <div className="cvd-admin__actions">
          <Link className="cvd-btn cvd-btn--ghost" to={basePath}>
            ← {t(`${COPY_PREFIX}.backToList`)}
          </Link>
        </div>
      </div>

      {bannerMessage ? (
        <div className={`cvd-alert cvd-alert--${bannerTone === "warning" ? "warning" : "success"}`}>{bannerMessage}</div>
      ) : null}

      <div className="cvd-run-detail__grid">
        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.summary`)}</h2>
          <dl className="cvd-dl">
            <div>
              <dt>{t(`${COPY_PREFIX}.table.reference`)}</dt>
              <dd>{request?.trackingReference || "—"}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.vendor`)}</dt>
              <dd>{request?.vendorName || request?.vendorId || "—"}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.submitted`)}</dt>
              <dd>{formatDateTime(request?.submittedAt, locale)}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.identityVerification`)}</dt>
              <dd>{request?.identityVerificationStatus || "—"}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.due`)}</dt>
              <dd>{formatDateTime(request?.dueAt, locale)}</dd>
            </div>
          </dl>
        </section>

        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.description`)}</h2>
          <p className="cvd-privacy-description">{request?.description || t(`${COPY_PREFIX}.detail.noDescription`)}</p>
        </section>
      </div>

      <section className="cvd-panel cvd-privacy-decisions">
        <h2>{t(`${COPY_PREFIX}.detail.decisions`)}</h2>
        <div className="cvd-privacy-decisions__primary">
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.PRIVACY_REQUESTS_RESOLVE}>
            <button type="button" className="cvd-btn cvd-btn--secondary" disabled={busy} onClick={runPreview}>
              {t(`${COPY_PREFIX}.detail.preview`)}
            </button>
            <button
              type="button"
              className="cvd-btn cvd-btn--primary"
              disabled={busy}
              onClick={() => resolve("approved", t(`${COPY_PREFIX}.detail.resolveSuccess`))}
            >
              {t(`${COPY_PREFIX}.detail.resolve`)}
            </button>
            <button
              type="button"
              className="cvd-btn cvd-btn--ghost"
              disabled={busy}
              onClick={() => resolve("rejected", t(`${COPY_PREFIX}.detail.rejectSuccess`))}
            >
              {t(`${COPY_PREFIX}.detail.reject`)}
            </button>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.PRIVACY_REQUESTS_SUPPRESS}>
            {request?.profileSuppressed ? (
              <button type="button" className="cvd-btn cvd-btn--secondary" disabled={busy} onClick={restoreProfile}>
                {t(`${COPY_PREFIX}.detail.restoreProfile`)}
              </button>
            ) : (
              <button type="button" className="cvd-btn cvd-btn--secondary" disabled={busy} onClick={suppressProfile}>
                {t(`${COPY_PREFIX}.detail.suppressProfile`)}
              </button>
            )}
          </VendorDirectoryPermissionGate>
        </div>

        {preview ? (
          <div className="cvd-claim-preview muted">
            {t(`${COPY_PREFIX}.detail.previewTitle`)} · {t(`${COPY_PREFIX}.detail.previewVersion`)}:{" "}
            <strong>{preview.previewVersion}</strong>
          </div>
        ) : null}
      </section>

      {detail?.fieldSuppressions?.length ? (
        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.fieldSuppressions`)}</h2>
          <ul className="cvd-privacy-suppressions">
            {detail.fieldSuppressions.map((item) => (
              <li key={item.id}>
                <strong>{item.fieldKey}</strong>
                <span className="muted">{item.resourceType}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {detail?.events?.length ? (
        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.history`)}</h2>
          <ul className="cvd-credential-history">
            {detail.events.map((event) => {
              const eventType = eventField(event, "event_type", "eventType");
              const createdAt = eventField(event, "created_at", "createdAt");
              return (
                <li key={event.id}>
                  <strong>{t(`${COPY_PREFIX}.events.${eventType}`, {}, eventType)}</strong>
                  <span className="muted">{formatDateTime(createdAt, locale)}</span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
